import { Component } from "@angular/core";
import { OpportunityService } from "../../../services/opportunity.service";
import { Opportunity } from "../../../models/opportunity.model";
import { DatePipe, NgIf } from "@angular/common";
import {
  CdkCell,
  CdkCellDef,
  CdkColumnDef,
  CdkHeaderCell,
  CdkHeaderCellDef,
  CdkHeaderRow,
  CdkHeaderRowDef,
  CdkRow,
  CdkRowDef,
  CdkTable,
  DataSource
} from "@angular/cdk/table";
import { Observable } from "rxjs";
import { DateAgoPipe } from "../../../pipes/date-ago.pipe";
import { IconDefinition } from "@fortawesome/fontawesome-common-types";
import { faCircle as faCircleRegular } from "@fortawesome/pro-regular-svg-icons/faCircle";
import { faCircleHalfStroke } from "@fortawesome/pro-solid-svg-icons/faCircleHalfStroke";
import { faCircleThreeQuartersStroke } from "@fortawesome/pro-solid-svg-icons/faCircleThreeQuartersStroke";
import { faCircle as faCircleSolid } from "@fortawesome/pro-solid-svg-icons/faCircle";
import { faCircleX } from "@fortawesome/pro-solid-svg-icons/faCircleX";

@Component({
  selector: "app-recent-opportunities",
  standalone: true,
  imports: [
    DatePipe,
    CdkTable,
    CdkColumnDef,
    CdkHeaderCell,
    CdkHeaderCellDef,
    CdkCell,
    CdkCellDef,
    CdkHeaderRow,
    CdkHeaderRowDef,
    CdkRow,
    CdkRowDef,
    DateAgoPipe,
    NgIf
  ],
  templateUrl: "./recent-opportunities.component.html"
})
export class RecentOpportunitiesComponent {
  protected readonly Date: DateConstructor = Date;
  protected dataSource: OpportunityDataSource = new OpportunityDataSource(this.opportunityService);
  protected columnsToDisplay: string[] = [
    "marketplaceStatus",
    "addDate",
    "age",
    "category",
    "linesOfBusiness",
    "industry",
    "employees",
    "yearsInBusiness",
    "estimatedPremium",
    "location"
  ];
  protected icons: { [id: string]: IconDefinition } = {
    Open: faCircleRegular,
    "In Progress": faCircleHalfStroke,
    "Quote Sent": faCircleThreeQuartersStroke,
    "Bound Prospect": faCircleSolid,
    "Unable to Service": faCircleX
  };
  protected iconColor: { [id: string]: string } = {
    Open: "text-slate-500",
    "In Progress": "text-yellow-600",
    "Quote Sent": "text-yellow-600",
    "Bound Prospect": "text-green-700",
    "Unable to Service": "hidden"
  };
  protected statusDisplay: { [id: string]: string } = {
    Open: "Open",
    "In Progress": "In Progress",
    "Quote Sent": "Quote Sent",
    "Bound Prospect": "Bound",
    "Unable to Service": "Unable to Service"
  };

  constructor(private opportunityService: OpportunityService) {}

  getStatusClass(marketplaceStatus: string): string {
    switch (marketplaceStatus) {
      case "Settled":
        return "bg-red-50 text-red-700 ring-red-600/10";
      case "Available to Claim":
        return "bg-yellow-50 text-yellow-800 ring-yellow-600/20";
      case "Claimed Referral":
        return "bg-green-50 text-green-700 ring-green-600/20";
      case "Ineligible":
        return "bg-pink-50 text-pink-700 ring-pink-600/20";
      case "Assigned CCR":
      default:
        return "bg-slate-50 text-slate-600 ring-slate-500/10";
    }
  }

  isToday(date: Date): boolean {
    if (date == null) {
      return false;
    }
    return new Date().setHours(0, 0, 0, 0) == new Date(date).setHours(0, 0, 0, 0);
  }

  timeAgo(dateAdded: Date): string {
    let currentDate: number = Date.now();
    let pastDate: number = new Date(dateAdded).valueOf();
    let duration: number = currentDate - pastDate;

    if (duration > 1000 * 60 * 60 * 24) {
      return `${Math.floor(duration / (1000 * 60 * 60 * 24))} days ago`;
    } else if (duration > 1000 * 60 * 60) {
      return `${Math.floor(duration / (1000 * 60 * 60))} hours ago`;
    } else if (duration > 1000 * 60) {
      return `${Math.floor(duration / (1000 * 60))} minutes ago`;
    } else if (duration > 1000) {
      return `${Math.floor(duration / 1000)} seconds ago`;
    } else {
      return "New";
    }
  }
}

export class OpportunityDataSource extends DataSource<Opportunity> {
  constructor(private opportunityService: OpportunityService) {
    super();
  }

  connect(): Observable<Opportunity[]> {
    return this.opportunityService.getRecentOpportunities();
  }

  disconnect(): void {}
}
